/**
 * useAdminFinancials.ts
 * 
 * Custom hook for managing admin financial data and state.
 * Separates business logic from UI components for better maintainability.
 */

import { useState, useEffect } from 'react'
import { adminDashboardApi } from '../services/adminApi'
import { AdminFinancials as AdminFinancialsType } from '../types/admin'

interface FinancialData extends AdminFinancialsType {
  // Additional computed fields for display
  revenueGrowth: number
  subscriptionGrowth: number
  creditGrowth: number
  averageRevenuePerUser: number
  recentTransactions: Array<{
    id: string
    user: string
    amount: number
    type: 'subscription' | 'credit' | 'refund'
    status: 'completed' | 'pending' | 'failed'
    date: string
  }>
}

interface UseAdminFinancialsReturn {
  financialData: FinancialData | null
  loading: boolean
  selectedPeriod: string
  setSelectedPeriod: (period: string) => void
  selectedView: string
  setSelectedView: (view: string) => void
  error: string | null
}

export const useAdminFinancials = (): UseAdminFinancialsReturn => {
  const [financialData, setFinancialData] = useState<FinancialData | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedPeriod, setSelectedPeriod] = useState('30d')
  const [selectedView, setSelectedView] = useState('overview')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadFinancialData = async () => {
      try {
        setLoading(true)
        setError(null)
        const apiData = await adminDashboardApi.getFinancials(selectedPeriod)
        
        // Transform API data to match component expectations
        const transformedData: FinancialData = {
          ...apiData,
          revenueGrowth: 12.5, // Mock growth percentage
          subscriptionGrowth: 8.3,
          creditGrowth: 15.7,
          averageRevenuePerUser: 245.67,
          recentTransactions: [
            {
              id: 'txn-1',
              user: 'John Creator',
              amount: 2345.67,
              type: 'subscription',
              status: 'completed',
              date: '2024-01-15'
            },
            {
              id: 'txn-2',
              user: 'Sarah Developer',
              amount: 1876.54,
              type: 'credit',
              status: 'completed',
              date: '2024-01-14'
            },
            {
              id: 'txn-3',
              user: 'Mike Designer',
              amount: 1543.21,
              type: 'subscription',
              status: 'pending',
              date: '2024-01-13'
            },
            {
              id: 'txn-4',
              user: 'Lisa Writer',
              amount: 1234.56,
              type: 'credit',
              status: 'completed',
              date: '2024-01-12'
            },
            {
              id: 'txn-5',
              user: 'Tom Artist',
              amount: 987.65,
              type: 'subscription',
              status: 'failed',
              date: '2024-01-11'
            }
          ]
        }
        
        setFinancialData(transformedData)
      } catch (error) {
        console.error('Failed to load financial data:', error)
        setError('Failed to load financial data')
      } finally {
        setLoading(false)
      }
    }

    loadFinancialData()
  }, [selectedPeriod])

  return {
    financialData,
    loading,
    selectedPeriod,
    setSelectedPeriod,
    selectedView,
    setSelectedView,
    error
  }
} 